import { useEffect, useRef, useState, CSSProperties } from 'react';

/**
 * Dev-only overlay to diagnose z-index stacking and pointer interception.
 * Mount once at the app root behind an `import.meta.env.DEV` guard.
 *
 * Features:
 *  1. Live scan of fixed/sticky elements with non-auto z-index.
 *  2. pointerdown capture probe — shows which element actually receives taps.
 *  3. Health check for the bottom-nav More button (data-debug-id="bottom-nav-more").
 */

type LayerInfo = {
  el: Element;
  tag: string;
  cls: string;
  z: number;
  pos: string;
};

type PointerEventInfo = {
  ts: number;
  x: number;
  y: number;
  tag: string;
  cls: string;
  z: string;
  isMore: boolean;
  blockingMore: boolean;
};

type MoreHealth =
  | { mounted: false }
  | {
      mounted: true;
      rect: DOMRect;
      z: string;
      hitTag: string;
      hitCls: string;
      hitIsButton: boolean;
    };

const MAX_EVENTS = 5;
const SCAN_INTERVAL_MS = 1000;

function describe(el: Element): { tag: string; cls: string } {
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute('class') || '').slice(0, 60);
  return { tag, cls };
}

function nearestZAncestor(el: Element | null): Element | null {
  let cur: Element | null = el;
  while (cur && cur !== document.body) {
    const cs = window.getComputedStyle(cur);
    if (cs.zIndex !== 'auto' && cs.zIndex !== '') return cur;
    cur = cur.parentElement;
  }
  return el;
}

function highlight(el: Element) {
  const htmlEl = el as HTMLElement;
  const prevOutline = htmlEl.style.outline;
  const prevOffset = htmlEl.style.outlineOffset;
  htmlEl.style.outline = '2px dashed #ff00ff';
  htmlEl.style.outlineOffset = '-2px';
  setTimeout(() => {
    htmlEl.style.outline = prevOutline;
    htmlEl.style.outlineOffset = prevOffset;
  }, 2000);
}

export default function ZIndexDebugOverlay() {
  const [open, setOpen] = useState(false);
  const [layers, setLayers] = useState<LayerInfo[]>([]);
  const [events, setEvents] = useState<PointerEventInfo[]>([]);
  const [moreHealth, setMoreHealth] = useState<MoreHealth>({ mounted: false });
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Scan layers + more health while open
  useEffect(() => {
    if (!open) return;
    const scan = () => {
      const all = document.querySelectorAll('body *');
      const found: LayerInfo[] = [];
      all.forEach((el) => {
        const cs = window.getComputedStyle(el);
        if ((cs.position === 'fixed' || cs.position === 'sticky') && cs.zIndex !== 'auto' && cs.zIndex !== '') {
          const z = parseInt(cs.zIndex, 10);
          if (!Number.isNaN(z)) {
            const { tag, cls } = describe(el);
            found.push({ el, tag, cls, z, pos: cs.position });
          }
        }
      });
      found.sort((a, b) => b.z - a.z);
      setLayers(found.slice(0, 30));

      // More health
      const more = document.querySelector('[data-debug-id="bottom-nav-more"]') as HTMLElement | null;
      if (!more) {
        setMoreHealth({ mounted: false });
      } else {
        const rect = more.getBoundingClientRect();
        const cs = window.getComputedStyle(more);
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const hit = document.elementFromPoint(cx, cy);
        const desc = hit ? describe(hit) : { tag: 'none', cls: '' };
        setMoreHealth({
          mounted: true,
          rect,
          z: cs.zIndex,
          hitTag: desc.tag,
          hitCls: desc.cls,
          hitIsButton: !!hit && (more === hit || more.contains(hit)),
        });
      }
    };
    scan();
    const id = window.setInterval(scan, SCAN_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [open]);

  // Pointerdown capture probe while open
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      // Ignore taps on the overlay itself
      const target = e.target as Element | null;
      if (target && target.closest('[data-debug-overlay]')) return;
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      const more = document.querySelector('[data-debug-id="bottom-nav-more"]');
      const moreRect = more?.getBoundingClientRect();
      const insideMoreRect =
        !!moreRect &&
        e.clientX >= moreRect.left &&
        e.clientX <= moreRect.right &&
        e.clientY >= moreRect.top &&
        e.clientY <= moreRect.bottom;
      const isMore = !!hit && !!more && (more === hit || more.contains(hit));
      const zEl = nearestZAncestor(hit);
      const cs = zEl ? window.getComputedStyle(zEl) : null;
      const desc = hit ? describe(hit) : { tag: 'none', cls: '' };
      const info: PointerEventInfo = {
        ts: Date.now(),
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        tag: desc.tag,
        cls: desc.cls,
        z: cs?.zIndex ?? 'auto',
        isMore,
        blockingMore: insideMoreRect && !isMore,
      };
      const next = [info, ...eventsRef.current].slice(0, MAX_EVENTS);
      setEvents(next);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  const pillStyle: CSSProperties = {
    position: 'fixed',
    right: 8,
    bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    zIndex: 9999,
    background: '#111',
    color: '#fff',
    border: '1px solid #444',
    borderRadius: 999,
    padding: open ? '0' : '6px 10px',
    fontFamily: 'ui-monospace, monospace',
    fontSize: 11,
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    right: 8,
    bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    zIndex: 9999,
    width: 'min(320px, calc(100vw - 16px))',
    maxHeight: '60vh',
    overflowY: 'auto',
    background: 'rgba(10,10,10,0.96)',
    color: '#fff',
    border: '1px solid #444',
    borderRadius: 8,
    padding: 8,
    fontFamily: 'ui-monospace, monospace',
    fontSize: 11,
    lineHeight: 1.4,
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
  };

  const sectionTitle: CSSProperties = {
    fontWeight: 700,
    fontSize: 10,
    color: '#9cf',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 8,
    marginBottom: 4,
  };

  const rowStyle: CSSProperties = {
    padding: '2px 4px',
    borderRadius: 3,
    cursor: 'pointer',
    wordBreak: 'break-all',
  };

  if (!open) {
    return (
      <button
        data-debug-overlay
        style={pillStyle}
        onClick={() => setOpen(true)}
        title="Open z-index debug overlay"
      >
        🐛 z
      </button>
    );
  }

  return (
    <div data-debug-overlay style={panelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700 }}>z-debug</span>
        <button
          onClick={() => setOpen(false)}
          style={{
            background: 'transparent',
            color: '#fff',
            border: '1px solid #555',
            borderRadius: 4,
            padding: '2px 6px',
            cursor: 'pointer',
            fontSize: 10,
          }}
        >
          close
        </button>
      </div>

      <div style={sectionTitle}>More button health</div>
      {!moreHealth.mounted ? (
        <div style={{ color: '#f88' }}>✗ Not mounted (sidebar open?)</div>
      ) : (
        <div>
          <div style={{ color: moreHealth.hitIsButton ? '#8f8' : '#f88' }}>
            {moreHealth.hitIsButton ? '✓ Clickable' : '✗ Blocked'} · z={moreHealth.z}
          </div>
          <div style={{ color: '#aaa', fontSize: 10 }}>
            rect: {Math.round(moreHealth.rect.left)},{Math.round(moreHealth.rect.top)} ·{' '}
            {Math.round(moreHealth.rect.width)}×{Math.round(moreHealth.rect.height)}
          </div>
          {!moreHealth.hitIsButton && (
            <div style={{ color: '#fbb', fontSize: 10 }}>
              hit: &lt;{moreHealth.hitTag}&gt; {moreHealth.hitCls}
            </div>
          )}
        </div>
      )}

      <div style={sectionTitle}>Last taps ({events.length})</div>
      {events.length === 0 ? (
        <div style={{ color: '#888' }}>tap anywhere…</div>
      ) : (
        events.map((ev, i) => (
          <div
            key={i}
            style={{
              ...rowStyle,
              background: ev.blockingMore ? 'rgba(255,80,80,0.18)' : 'transparent',
              color: ev.isMore ? '#8f8' : '#fff',
            }}
          >
            ({ev.x},{ev.y}) z={ev.z} &lt;{ev.tag}&gt; {ev.cls}
            {ev.isMore && ' · MORE ✓'}
            {ev.blockingMore && ' · BLOCKS MORE'}
          </div>
        ))
      )}

      <div style={sectionTitle}>Fixed/sticky layers ({layers.length})</div>
      {layers.map((l, i) => (
        <div
          key={i}
          style={rowStyle}
          onClick={() => highlight(l.el)}
          title="Click to highlight in page"
        >
          <span style={{ color: '#9cf' }}>z={l.z}</span> ({l.pos}) &lt;{l.tag}&gt; {l.cls}
        </div>
      ))}
    </div>
  );
}
